import React, { useState } from "react";
import { Plus, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { scoreHex, readinessLabel, HIGHLIGHT_SUGGESTIONS, QR_OPTIONS } from "@/lib/careerBuilder";
import { WsSection, TagGroup } from "@/components/negotiation/workspace/shared";
import { translateFr as tfr } from "@/i18n";

const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/* ---------- STEP 5 — Career readiness (auto + override) & signals ---------- */
export function StepReadiness({ draft, update, catalogs, meta }) {
  const overrides = draft.readiness_overrides || {};
  const auto = meta?.auto_readiness || {};
  const dims = meta?.dims || {};
  const [customHl, setCustomHl] = useState("");

  return (
    <div data-testid="builder-step-readiness">
      <WsSection title="Career readiness" subtitle={tfr("Calculé automatiquement à partir des données disponibles — ajustez si nécessaire.")}>
        <div className="rounded-xl border border-border bg-secondary/30 p-3.5 mb-3">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl font-bold tabular-nums" style={{ color: scoreHex(meta?.overall) }} data-testid="readiness-overall">
              {meta?.overall != null ? `${meta.overall}%` : "—"}
            </span>
            <div>
              <p className="text-xs font-semibold">{readinessLabel(meta?.overall)}</p>
              <p className="text-[11px] text-muted-foreground">Career Velocity : <span className="font-semibold tabular-nums">{meta?.velocity ?? "—"}</span>/100</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
            {(catalogs?.readiness_dims || []).map((d) => {
              const v = dims[d.id];
              const overridden = overrides[d.id] !== undefined && overrides[d.id] !== null;
              return (
                <div key={d.id} className="flex items-center gap-2" data-testid={`readiness-dim-${d.id}`}>
                  <span className="text-xs w-28 shrink-0 truncate">{d.label}</span>
                  <span className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                    {v != null && <span className="block h-full rounded-full" style={{ width: `${v}%`, background: scoreHex(v) }} />}
                  </span>
                  <Input type="number" min={0} max={100} value={overrides[d.id] ?? ""}
                    placeholder={auto[d.id] != null ? String(auto[d.id]) : "—"}
                    onChange={(e) => {
                      const next = { ...overrides };
                      if (e.target.value === "") delete next[d.id];
                      else next[d.id] = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                      update(["readiness_overrides"], next);
                    }}
                    className="h-8 w-[62px] text-xs tabular-nums" aria-label={`${d.label} (override)`}
                    data-testid={`readiness-override-${d.id}`} />
                  {overridden && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={tfr("Revenir à la valeur automatique")}
                      onClick={() => { const next = { ...overrides }; delete next[d.id]; update(["readiness_overrides"], next); }}
                      data-testid={`readiness-reset-${d.id}`}><RotateCcw className="h-3 w-3" /></Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </WsSection>

      <WsSection title={tfr("Points de vigilance")} subtitle={tfr("Cliquez pour inclure — apparaissent dans l'encart rouge du PDF.")}>
        <TagGroup options={catalogs?.risks || []} selected={draft.risks || []} max={7}
          onToggle={(id) => update(["risks"], toggle(draft.risks || [], id))} testidPrefix="risk-chip" />
      </WsSection>

      <WsSection title={tfr("Opportunités")} subtitle={tfr("Sélection multiple.")}>
        <TagGroup options={catalogs?.opportunities || []} selected={draft.opportunities || []} max={8}
          onToggle={(id) => update(["opportunities"], toggle(draft.opportunities || [], id))} testidPrefix="opp-chip" />
      </WsSection>

      <WsSection title="Career highlights" subtitle={tfr("Suggestions issues de l'historique — cliquez pour inclure.")}>
        <div className="flex flex-wrap gap-2 mb-2.5">
          {HIGHLIGHT_SUGGESTIONS.filter((s) => !(draft.highlights || []).includes(s)).map((s) => (
            <button key={s} type="button" onClick={() => update(["highlights"], [...(draft.highlights || []), s])}
              data-testid={`highlight-suggest-${s.slice(0, 12)}`}
              className="rounded-full border border-dashed border-border px-3 h-8 text-xs text-muted-foreground hover:text-foreground hover:border-athlynx/40 transition-colors">
              + {s}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-2.5">
          {(draft.highlights || []).map((h) => (
            <button key={h} type="button" onClick={() => update(["highlights"], draft.highlights.filter((x) => x !== h))}
              title={tfr("Retirer")} data-testid={`highlight-active-${h.slice(0, 12)}`}
              className="rounded-full bg-athlynx text-white border border-athlynx px-3 h-8 text-xs font-medium">
              ✓ {h}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={customHl} onChange={(e) => setCustomHl(e.target.value)} placeholder={tfr("Ajouter un fait marquant…")}
            className="h-9 text-sm" maxLength={140} data-testid="highlight-custom-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && customHl.trim()) {
                update(["highlights"], [...(draft.highlights || []), customHl.trim()]);
                setCustomHl("");
              }
            }} />
          <Button variant="outline" size="sm" className="h-9 gap-1 text-xs shrink-0" data-testid="highlight-custom-add"
            onClick={() => { if (customHl.trim()) { update(["highlights"], [...(draft.highlights || []), customHl.trim()]); setCustomHl(""); } }}>
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </Button>
        </div>
      </WsSection>

      <WsSection title={tfr("Forces du profil")} subtitle="8 maximum.">
        <TagGroup options={catalogs?.strengths || []} selected={draft.strengths || []} max={8}
          onToggle={(id) => update(["strengths"], toggle(draft.strengths || [], id))} testidPrefix="strength-chip" />
      </WsSection>

      <WsSection title={tfr("Style de jeu")} subtitle="5 maximum.">
        <TagGroup options={catalogs?.styles || []} selected={draft.styles || []} max={5}
          onToggle={(id) => update(["styles"], toggle(draft.styles || [], id))} testidPrefix="style-chip" />
      </WsSection>

      <WsSection title={tfr("Environnement recherché")} subtitle="6 maximum.">
        <TagGroup options={catalogs?.environments || []} selected={draft.environments || []} max={6}
          onToggle={(id) => update(["environments"], toggle(draft.environments || [], id))} testidPrefix="env-chip" />
      </WsSection>
    </div>
  );
}

/* ---------- STEP 6 — Final page (agent contact + QR) ---------- */
export function StepContact({ draft, update }) {
  const ct = draft.contact || {};
  const qr = draft.qr || {};
  const fields = [
    ["name", "Nom de l'agent"], ["agency", "Agence"], ["email", "Email"],
    ["phone", tfr("Téléphone")], ["website", "Site web"],
  ];
  return (
    <div data-testid="builder-step-contact">
      <WsSection title="Contact agent" subtitle={tfr("Pré-rempli depuis votre compte — affiché sur la page finale du PDF.")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {fields.map(([key, label]) => (
            <div key={key} className={cn(key === "website" && "sm:col-span-2")}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</p>
              <Input value={ct[key] || ""} onChange={(e) => update(["contact", key], e.target.value)}
                className="h-9 text-sm" data-testid={`contact-${key}`} />
            </div>
          ))}
        </div>
      </WsSection>
      <WsSection title="QR code" subtitle={tfr("Destination du QR code imprimé sur le document.")}>
        <RadioGroup value={qr.destination || "athlete"} onValueChange={(v) => update(["qr", "destination"], v)}
          className="space-y-1.5" data-testid="qr-destination-group">
          {QR_OPTIONS.map((o) => (
            <div key={o.id} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2.5">
              <RadioGroupItem value={o.id} id={`qr-${o.id}`} data-testid={`qr-option-${o.id}`} />
              <Label htmlFor={`qr-${o.id}`} className="text-sm cursor-pointer flex-1">{o.label}</Label>
            </div>
          ))}
        </RadioGroup>
        {qr.destination === "custom" && (
          <Input value={qr.custom_url || ""} onChange={(e) => update(["qr", "custom_url"], e.target.value)}
            placeholder="https://…" className="h-9 text-sm mt-2.5" data-testid="qr-custom-url" />
        )}
      </WsSection>
    </div>
  );
}
